import { Link, Stack, Text } from '@chakra-ui/react';

import { buildEmployerEmailTemplate, ENTERPRISE_OVERVIEW_URL } from '../utils/email-templates';

import { CopyToText } from './copy-to-text';
import { SendPictogram } from './reimbursement-pictograms';
import { SectionHeading } from './section-heading';

export function EmailEmployerSection({ monthlyPrice }: { monthlyPrice: string }) {
  return (
    <Stack as='section' color='grey.700' fontSize='16px' gap={8} lineHeight='1.5'>
      <SectionHeading colorScheme='blue' pictogram={<SendPictogram />} title='Email your employer' singleLineOnMobile />
      <Stack gap={6}>
        <Text>
          You will need to speak with your company to figure out the approval process for reimbursement. Some companies
          have an education benefit you can use that is managed by HR or other departments, while other companies will
          need specific approval by your manager.
        </Text>
        <Text>
          Here is an example email template you can use to let your manager know you’d like your education costs with
          Udacity reimbursed:
        </Text>
      </Stack>
      <CopyToText label='Email To Employer' name='employer-template' text={buildEmployerEmailTemplate(monthlyPrice)}>
        <Text>
          Hi{' '}
          <Text as='span' color='blue.500' fontWeight='semibold'>
            [manager name]
          </Text>
          ,
        </Text>
        <Text>
          I&apos;d like to enroll in a program called{' '}
          <Text as='span' color='blue.500' fontWeight='semibold'>
            [program name]
          </Text>{' '}
          on the professional education platform Udacity. Programs at Udacity consist of a high-quality, meticulously
          designed curriculum, built in collaboration with companies like Google, Facebook, Amazon, Adobe, and
          Microsoft.
        </Text>
        <Text>
          If you’d like to review what I will learn in{' '}
          <Text as='span' color='blue.500' fontWeight='semibold'>
            [program name]
          </Text>
          , please take a look here:{' '}
          <Text as='span' color='blue.500' fontWeight='semibold'>
            [link to course]
          </Text>
        </Text>
        <Text>
          Udacity’s learning subscription is unique because I’ll learn via hands-on projects that allow me to apply my
          new skills in the workplace immediately. I’ll also have access to a mentor network comprised of industry
          experts I can get feedback from throughout my learning journey.
        </Text>
        <Text>
          This new knowledge will help our team by{' '}
          <Text as='span' color='blue.500' fontWeight='semibold'>
            [list reasons]
          </Text>
        </Text>
        <Text>
          A Udacity subscription costs {monthlyPrice} per month. This gets me access to every program on their platform
          from schools such as Artificial Intelligence, Data Science, and Programming. I will be able to expand my
          knowledge in multiple areas beneficial to the team through continuous education.
        </Text>
        <Text>What are your thoughts?</Text>
        <Text>
          Thanks,
          <br />
          <Text as='span' color='blue.500' fontWeight='semibold'>
            [Your Name]
          </Text>
        </Text>
        <Text>
          P.S. Udacity has business plans that allow for entire teams to upskill at once. It might make sense for us to
          sign up for a plan like this. You can learn more here:{' '}
          <Link
            color='blue.500'
            fontWeight='semibold'
            href={ENTERPRISE_OVERVIEW_URL}
            textDecoration='underline'
            isExternal
          >
            {ENTERPRISE_OVERVIEW_URL}
          </Link>
        </Text>
      </CopyToText>
    </Stack>
  );
}
